import React, { useState } from 'react';
import { VerificationDisplay, ScannedTTR } from './types';
import { VerifyAccent } from './theme';
import { conditionLabel } from './condition';
import Silhouette from './Silhouette';

interface Props {
  display: VerificationDisplay;
  scanned: ScannedTTR;
  accent: VerifyAccent;
}

/**
 * Item identity card. Canonical position data (name / set / image / description)
 * comes from the mirror doc; the CONDITION shown is the value actually scanned
 * from the sticker (Â§7.1) so a graded copy reads correctly even when resolved
 * via the canonical fallback record.
 */
const VerifyIdentity: React.FC<Props> = ({ display, scanned, accent }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const img = display.imageUrl || display.imageUrlObverse || null;
  const showImage = img && !imageFailed;
  const cond = conditionLabel(scanned.condition);

  return (
    <div className={`border-x border-gray-200 px-5 py-6 ${accent.panel}`}>
      <div className="flex flex-col sm:flex-row gap-5">
        {/* Image / per-board silhouette fallback (D4) */}
        <div className="flex-shrink-0 mx-auto sm:mx-0">
          {showImage ? (
            <img
              src={img as string}
              alt={display.positionLabel || 'Registered collectible'}
              className="w-40 h-56 object-contain rounded bg-white border border-gray-200"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <Silhouette boardCategory={display.boardCategory} />
          )}
        </div>

        {/* Identity text */}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight break-words">
            {display.positionLabel || 'Registered Item'}
          </h1>
          {display.positionSubtitle && (
            <p className="text-gray-600 mt-1 break-words">{display.positionSubtitle}</p>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {cond && (
              <span className="inline-flex items-center rounded bg-gray-900 text-white text-sm font-medium px-2.5 py-1">
                Condition: {cond}
              </span>
            )}
            {display.keyStatus && (
              <span className={`inline-flex items-center rounded text-xs font-semibold px-2 py-1 ${accent.badge}`}>
                {display.keyStatus}
              </span>
            )}
            <span className="text-sm text-gray-500">Copy {scanned.copy}</span>
          </div>

          {display.positionDescription && (
            <p className="text-sm text-gray-700 mt-4 leading-relaxed">
              {display.positionDescription}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyIdentity;
